// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import { MerchantProgramme, MerchantProgramType } from 'src/@types/merchant';
import { lowerCase, readableStringFormatter } from 'src/utils/stringFormatter';
import {
  FileNavIcon,
  ProfileNavIcon,
  GlobNavIcon,
  CheckIcon,
  SettingsIcon,
  CO2Icon,
  CreditsIcon,
  TextIcon,
  CustomIcon,
  GlobeIcon,
  ConfigurationIcon,
  FlightIcon,
} from 'src/theme/overrides/CustomIcons';
import { IconBuilding, IconCalculator, IconChart, IconCoin, IconGlob, IconMapPoint, IconMultiMapPoint, IconSettings, IconSmile, IconSquares } from 'src/libraries/icons';
import { ProgrammeType } from 'src/@types/reporting';
import { NavListProps } from './types';
import { FeatureTypes } from 'src/@types/feature';

// ----------------------------------------------------------------------

const generateRouteChildren = (el: any) => {
  const repeatableEachProgrammeLinks = [
    {
      title: 'Overview',
      path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code),
      id: 'nav-item-engagement',
      icon: <CheckIcon />
    },
    {
      title: 'Contributions',
      path: PATH_DASHBOARD.programs.transactions(lowerCase(el.type), el.code),
      id: 'nav-item-collective-credits',
      icon: <FileNavIcon />
    },
    {
      title: 'Impact',
      path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code),
      id: 'nav-item-impact',
      icon: <GlobNavIcon />
    },
  ];

  const children: { [key: string]: NavListProps[] } = {
    CORPORATE_FLIGHT: [
      {
        title: 'Overview',
        path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code),
        id: 'nav-item-light-corporate-engagement',
        icon: <CheckIcon />
      },
      {
        title: 'Emissions',
        path: PATH_DASHBOARD.programs.emissions(lowerCase(el.type), el.code),
        hideDatePicker: true,
        id: 'nav-item-flight-corporate-emissions',
        icon: <CO2Icon />
      },
      {
        title: 'Contributions',
        path: PATH_DASHBOARD.programs.transactions(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-contributions',
        icon: <FileNavIcon />
      },
      {
        title: 'Impact',
        path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-impact',
        icon: <GlobNavIcon />
      },
      {
        title: 'Flights',
        path: PATH_DASHBOARD.programs.flights(lowerCase(el.type), el.code),
        id: 'nav-item-flights',
        icon: <FlightIcon />
      },
      {
        title: 'Emission record',
        path: PATH_DASHBOARD.programs.emissionRecord(lowerCase(el.type), el.code, '[a-zA-Z0-9-]*'),
        id: 'nav-item-flight-corporate-emission-record',
        hide: true,
        hideDatePicker: true,
        isDynamicPath: true
      },
      {
        title: 'Add estimate',
        path: PATH_DASHBOARD.programs.addEmission(lowerCase(el.type), el.code, '[a-zA-Z0-9-]*'),
        id: 'nav-item-flight-corporate-add-estimate',
        hide: true,
        hideDatePicker: true,
        isDynamicPath: true
      },
      {
        title: 'Calculate',
        path: PATH_DASHBOARD.programs.calculate(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-calculate',
        hide: true,
        hideDatePicker: true,
      },
    ],
    FLIGHT: [
      // repeatableEachProgrammeLinks - sharable items across every menu programme
      ...repeatableEachProgrammeLinks,
      {
        title: 'Travellers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    JOURNEY: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Customers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    EMPLOYEE: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Employees',
        path: PATH_DASHBOARD.programs.employees(lowerCase(el.type), el.code),
        id: 'nav-item-employees',
        icon: <ProfileNavIcon />
      },
      {
        title: 'Configuration',
        path: PATH_DASHBOARD.programs.configuration(lowerCase(el.type), el.code),
        id: 'nav-item-configuration',
        icon: <ConfigurationIcon />
      },
    ],
  }

  return children[el.type];
}

export interface ProgrammeMenu {
  title: string;
  code: string;
  path: string;
  id: string;
  icon: JSX.Element;
  programme: keyof typeof MerchantProgramType;
  hideDatePicker?: boolean;
  programmeMenu: NavListProps[];
}

const getProgrammeDefaultSubtitle = (programmeType: ProgrammeType) => {
  switch(programmeType) {
    case ProgrammeType.FLIGHT:
      return 'Individuals calculate and offset their flight emissions.';
    case ProgrammeType.EMPLOYEE:
      return 'Individuals can measure, reduce, and offset the carbon footprint in a branded portal.';
    case ProgrammeType.CORPORATE_FLIGHT:
      return 'Corporates calculate and offset their flight emissions.';
    case ProgrammeType.JOURNEY:
      return 'Individuals calculate the carbon footprint of their journey including flight, ground transport, and accomodation.';
    default:
      return '';
  }
}

export const getProgramMenu = (programmes: MerchantProgramme[]) => {
  const menu: ProgrammeMenu[] = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const title = el.name || el.type;
    return {
      title: readableStringFormatter(title),
      code: el.code,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      defaultPath: children[0].path,
      id: `nav-item-programme-${el.code}`,
      icon: <IconSquares />,
      subtitle: el?.subtitle || getProgrammeDefaultSubtitle(el.type as ProgrammeType),
      programme: el.type,
      programmeMenu: children,
    }
  });

  return menu;
};

export const TLSettingsMenuItems = [
  {
    title: 'Metadata',
    path: '#generalSettings', // equals to section id
    icon: <SettingsIcon />,
  },
  {
    title: 'Header',
    path: '#headerSection',
    icon: <TextIcon />,
  },
  {
    title: 'Intro',
    path: '#introSection',
    icon: <GlobeIcon />,
  },
  {
    title: 'Impact',
    path: '#impactSection',
    icon: <CO2Icon />,
  },
  {
    title: 'Explainer',
    path: '#howItWorksSection',
    icon: <CreditsIcon />,
  },
  {
    title: 'Custom',
    path: '#customSection',
    icon: <CustomIcon />,
  },
];

export const merchantStatsMenu = [
  { title: 'Impact', path: PATH_DASHBOARD.communications.impact, id: 'nav-item-impact', icon: <IconChart />, subtitle: 'See how your carbon emissions have been offset over time.' },
  { title: 'Supported projects', path: PATH_DASHBOARD.communications.projects, id: 'nav-item-supported-projects', icon: <IconMapPoint />, subtitle: 'View a list of all the initiatives that have offset your carbon emissions.' },
];

const navConfig = [
  {
    title: 'Projects',
    name: 'projects',
    path: '#',
    children: [
      { title: 'Projects', path: PATH_DASHBOARD.projects.root, id: 'nav-item-projects', icon: <IconMapPoint />, subtitle: 'Browse our verified carbon offsetting projects.' },
      { title: 'Portfolios', path: PATH_DASHBOARD.baskets.root, id: 'nav-item-portfolios', icon: <IconMultiMapPoint />, subtitle: 'Curated portfolios enable a balanced approach to offsetting by bundling multiple projects together.' },
    ],
  },
  {
    title: 'My offsets',
    name: 'my-offsets',
    path: '#',
    children: [
      { title: 'My offsets', path: PATH_DASHBOARD.offsets.transactions, id: 'nav-item-my-offsets', icon: <IconCoin />, subtitle: 'View your past carbon offset purchases.' },
      { title: 'My footprints', featureCode: FeatureTypes.FOOTPRINT, path: PATH_DASHBOARD.offsets.footprints, id: 'nav-item-my-footprints', icon: <IconCalculator />, subtitle: 'Calculate the carbon footprint of and manage your business emissions – from flights, freight, fuel use and more.' },
    ],
  },
  {
    title: 'Programmes',
    name: 'programmes',
    path: '#',
    children: [
      { title: 'Individuals', path: PATH_DASHBOARD.programs.individuals, id: 'nav-item-individuals', icon: <IconSmile />, subtitle: 'View individuals and employees who’ve engaged with your programmes.' },
      { title: 'Business customers', featureCode: FeatureTypes.MANAGE_BUSINESS_CUSTOMER, path: PATH_DASHBOARD.programs.businessCustomers, id: 'nav-item-business-customers', icon: <IconBuilding />, subtitle: 'Manage businesses that have participated in your programmes. Profiles may be added manually or created automatically.' },
      { title: 'Toolkit', featureCode: FeatureTypes.INTERACTIVE_TOOLS, path: PATH_DASHBOARD.programs.toolkit, id: 'nav-item-toolkit', icon: <IconSettings />, subtitle: 'Browse our toolkit.' },
    ],
  },
  {
    title: 'Communications',
    name: 'communications',
    path: '#',
    children: [
      ...merchantStatsMenu,
      { title: 'Landing pages', featureCode: FeatureTypes.LANDING_PAGE, path: PATH_DASHBOARD.communications.landingPages, id: 'nav-item-landing pages', icon: <IconGlob />, subtitle: 'Manage the landing pages your customers use to engage with your programmes.' },
      { title: 'Widgets', featureCode: FeatureTypes.IMPACT_WIDGET || FeatureTypes.CARBONCLICK_TILE_DYNAMIC, path: PATH_DASHBOARD.communications.widgets, id: 'nav-item-widgets', icon: <IconSettings />, subtitle: 'Embed widgets on your website to showcase your carbon offset impact.' },
    ],
  },
  {
    title: 'Settings',
    name: 'settings',
    hideOnDesktop: true,
    path: '#',
    children: [
      { title: 'General', path: PATH_DASHBOARD.settings.business, id: 'nav-item-settings-general', icon: <IconSquares /> },
      { title: 'Theme', path: PATH_DASHBOARD.settings.theme, id: 'nav-item-settings-theme', icon: <IconSquares /> },
      { title: 'Default basket', path: PATH_DASHBOARD.settings.defaultBasket, id: 'nav-item-settings-default-basket', icon: <IconSquares /> },
      { title: 'Billing', path: PATH_DASHBOARD.settings.billing, id: 'nav-item-settings-billing', icon: <IconSquares /> },
      { title: 'Receipt emails', path: PATH_DASHBOARD.settings.emails, id: 'nav-item-settings-emails', icon: <IconSquares /> },
      { title: 'Dashboard', path: PATH_DASHBOARD.settings.dashboard, id: 'nav-item-settings-dashboard', icon: <IconSquares /> },
    ],
  }
];

export const getNavConfig = (programmes: MerchantProgramme[] = [], merchantFeatures?: { code: string }[]) => {
  const programmeMenuItems = getProgramMenu(programmes);

  const menuWithProgrammes = navConfig.map((section) => {
    if (section.name === 'programmes') {
      return {
        ...section,
        children: [...section.children, ...programmeMenuItems],
      };
    }
    return section;
  })

  const newMenu = menuWithProgrammes.map((section) => {
    const updatedSection = {
      ...section,
      children: section.children?.map((item: any) => {
        return {
          ...item,
          disabled: !item?.featureCode ? false : !merchantFeatures?.find(feature => feature?.code === item?.featureCode)
        }
      }),
    };
    
    return updatedSection;
  })

  return newMenu;
}

export default navConfig;
